import { defineStore } from 'pinia';

import { addEntryToFavorites, getFavorites, removeEntriesFromFavorites } from '@/api/dictionary';
import { notify } from '@/notifications';
import { Entry, EntryList } from '@/types';
import { capitalize } from '@/utils/generalUtils';

interface State {
	entryList: EntryList | null;
	loading: boolean;
}

export const useFavouritesStore = defineStore('favourites', {
	state: (): State => ({
		entryList: null,
		loading: false,
	}),

	getters: {
		isFavourite: state => (id: number) => {
			return state.entryList?.entries?.some(e => e.id === id) || false;
		},
	},

	actions: {
		async fetchFavourites() {
			this.loading = true;
			try {
				const r = await getFavorites();
				this.entryList = {
					createdAt: new Date(r.createdAt),
					language: r.language,
					name: r.name,
					visibility: r.visibility,
					entries: r.entries,
					count: r.count,
					id: r.id,
				};
			} catch (err) {
				console.error('Failed to fetch favourites', err);
			} finally {
				this.loading = false;
			}
		},

		async removeEntry(entry: Entry) {
			if (!this.entryList) return;

			this.loading = true;
			try {
				await removeEntriesFromFavorites([entry.id]);
				this.entryList.entries = this.entryList.entries.filter(e => e.id !== entry.id);
				notify({
					message: `${capitalize(entry.headWord)} slettet fra Favoritter`,
					type: 'success',
					timeout: 2000,
				});
			} catch (err) {
				console.error('Failed to remove entry', err);
				notify({
					message: 'Noget gik galt ved fjernelse af glosen.',
					type: 'error',
				});
			} finally {
				this.loading = false;
			}
		},

		async addToFavorites(entry: Entry) {
			this.loading = true;

			try {
				await addEntryToFavorites(entry);
				this.entryList?.entries.push(entry);
				notify({
					message: `${capitalize(entry.headWord)} tilføjet til Favoritter`,
					type: 'success',
					timeout: 2000,
				});
			} catch (err) {
				console.error('Failed to add entry to favorites', err);
				notify({
					message: 'Noget gik galt ved tilføjelse af glosen.',
					type: 'error',
				});
			} finally {
				if (!this.entryList) {
					this.fetchFavourites();
				}
				this.loading = false;
			}
		},
	},
});
